import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Award, AlertCircle } from 'lucide-react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const LevelUpTest = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [targetLevel, setTargetLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadUserLevel();
  }, []);
  
  const loadUserLevel = async () => {
    try {
      const response = await axios.get(`${API}/exam/history`, {
        withCredentials: true
      });
      
      if (response.data.length === 0) {
        toast.error('Önce seviye tespit sınavını yapmalısınız');
        navigate('/dashboard');
        return;
      }
      
      const lastExam = response.data[0];
      const level = lastExam.determined_level;
      const levelIndex = LEVEL_ORDER.indexOf(level);
      
      if (levelIndex === LEVEL_ORDER.length - 1) {
        toast.info('Zaten en yüksek seviyedesiniz!');
        navigate('/dashboard');
        return;
      }
      
      setCurrentLevel(level);
      setTargetLevel(LEVEL_ORDER[levelIndex + 1]);
      setLoading(false);
    } catch (error) {
      toast.error('Seviye bilgisi alınamadı');
      navigate('/dashboard');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Dashboard'a Dön
        </Button>
        
        <Card className="glass-effect border-none shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <TrendingUp className="w-12 h-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl gradient-text">Seviye Atlama Testi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="px-6 py-3 bg-blue-100 rounded-lg">
                  <p className="text-sm text-gray-600">Mevcut Seviye</p>
                  <p className="text-3xl font-bold text-blue-600">{currentLevel}</p>
                </div>
                <ArrowLeft className="w-8 h-8 text-gray-400 transform rotate-180" />
                <div className="px-6 py-3 bg-green-100 rounded-lg">
                  <p className="text-sm text-gray-600">Hedef Seviye</p>
                  <p className="text-3xl font-bold text-green-600">{targetLevel}</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="font-semibold text-yellow-800 mb-2">Önemli Bilgiler:</p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Bu özellik yakında aktif olacak</li>
                      <li>• {targetLevel} seviyesine geçmek için özel test</li>
                      <li>• Başarı oranı %70 ve üzeri gerekli</li>
                      <li>• Şu an için konuşma pratiği ile gelişebilirsiniz</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="lg"
                className="flex-1"
              >
                Dashboard'a Dön
              </Button>
              <Button
                onClick={() => navigate('/speaking-practice')}
                size="lg"
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <Award className="w-5 h-5 mr-2" />
                Konuşma Pratiği Yap
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LevelUpTest;
